import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
};

// Products API
export const productsAPI = {
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// Categories API
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Orders API
export const ordersAPI = {
  getAll: () => api.get('/orders'),
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  updateStatus: (id, status) => api.patch(`/orders/${id}/status`, null, { params: { status } }),
  updateTracking: (id, data) => api.patch(`/orders/${id}/tracking`, data),
};

// Banners API
export const bannersAPI = {
  getAll: (activeOnly = true) => api.get('/banners', { params: { active_only: activeOnly } }),
  create: (data) => api.post('/banners', data),
  delete: (id) => api.delete(`/banners/${id}`),
};

// Wishlist API
export const wishlistAPI = {
  get: () => api.get('/wishlist'),
  add: (productId) => api.post('/wishlist', { product_id: productId }),
  remove: (productId) => api.delete(`/wishlist/${productId}`),
};

// Cart API
export const cartAPI = {
  get: () => api.get('/cart'),
  add: (data) => api.post('/cart', data),
  update: (id, quantity) => api.put(`/cart/${id}`, null, { params: { quantity } }),
  remove: (id) => api.delete(`/cart/${id}`),
};

// Contact API
export const contactAPI = {
  submit: (data) => api.post('/contact', data),
};

// Razorpay API (guest-friendly, no auth required)
export const razorpayAPI = {
  getKey: () => api.get('/razorpay/key'),
  createOrder: (amount) => api.post('/razorpay/create-order', { amount, currency: 'INR' }),
  verifyPayment: (data) => api.post('/razorpay/verify-payment', null, { params: data }),
};

export const uploadAPI = {
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};


// Exchange Rate API
export const exchangeRateAPI = {
  getRate: (base = 'INR', target = 'AED') => api.get('/exchange-rate', { params: { base, target } }),
};


// Reviews API
export const reviewsAPI = {
  getForProduct: (productId, params = {}) => api.get(`/reviews/product/${productId}`, { params }),
  getSummary: (productId) => api.get(`/reviews/product/${productId}/summary`),
  create: (data) => api.post('/reviews', data),
  markHelpful: (reviewId, voterId) => api.post(`/reviews/${reviewId}/helpful`, null, { params: { voter_id: voterId } }),
  verifyPurchase: (productId, email, orderId) => api.post('/reviews/verify-purchase', null, { params: { product_id: productId, email, order_id: orderId } }),
  uploadMedia: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/reviews/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Admin
  getAll: (status) => api.get('/reviews/admin', { params: status ? { status } : {} }),
  updateStatus: (reviewId, status) => api.patch(`/reviews/${reviewId}/status`, null, { params: { status } }),
  delete: (reviewId) => api.delete(`/reviews/${reviewId}`),
};
